import {
	IMPORT_ACCOUNTS_LOAD_BEGIN,
	IMPORT_ACCOUNTS_LOAD_SUCCESS,
	IMPORT_BACKFILL_BEGIN,
	IMPORT_BALANCES_LOAD_BEGIN,
	IMPORT_BALANCES_LOAD_SUCCESS,
	IMPORT_BATCHES_LOAD_BEGIN,
	IMPORT_BATCHES_LOAD_SUCCESS,
	IMPORT_BATCH_DELETED,
	IMPORT_BATCH_SELECTED,
	IMPORT_CATEGORIES_LOAD_BEGIN,
	IMPORT_CATEGORIES_LOAD_SUCCESS,
	IMPORT_CATEGORY_ROWS_CLOSED,
	IMPORT_CATEGORY_ROWS_LOAD_BEGIN,
	IMPORT_CATEGORY_ROWS_LOAD_SUCCESS,
	IMPORT_CATEGORY_ROWS_OPENED,
	IMPORT_COMMIT_BEGIN,
	IMPORT_COMMIT_SUCCESS,
	IMPORT_ERROR_DISMISSED,
	IMPORT_MONARCH_BALANCES_RECORDED,
	IMPORT_MONARCH_CONNECT_BEGIN,
	IMPORT_MONARCH_CONNECTION_CHANGED,
	IMPORT_MONARCH_FETCH_BEGIN,
	IMPORT_MONARCH_REFRESH_BEGIN,
	IMPORT_MONARCH_REFRESH_FAIL,
	IMPORT_MONARCH_REFRESH_SUCCESS,
	IMPORT_MONARCH_RULES_LOADED,
	IMPORT_PREVIEW_LOAD_BEGIN,
	IMPORT_PREVIEW_LOAD_SUCCESS,
	IMPORT_UPLOAD_BEGIN,
	IMPORT_UPLOAD_FAIL,
	IMPORT_UPLOAD_SUCCESS,
} from '../actions/import.js';
import { CHANGE_ACTIVE_BUDGET_SUCCESS } from '../actions/sharing.js';

const initialState = {
	batches: [],
	selectedBatch: null,
	/** Every account label the file used, with its current mapping and any suggestion. */
	accounts: [],
	/** The plaid accounts available to map on to. */
	availableAccounts: [],
	/** The account types a newly created account can be given. Comes from the API, not hardcoded here. */
	accountTypes: [],
	previewRows: [],
	previewOffset: 0,
	/** The status the preview is narrowed to, or null for every row. */
	previewStatus: null,
	/** How many rows the preview pages through: the whole file, or just those with that status. */
	previewTotal: 0,
	summary: null,
	/** Every (category, merchant) pair the file used, with the rule covering it. */
	categories: [],
	/** The budget categories a rule can point at, each carrying the group it sits in. */
	availableCategories: [],
	/**
	 * The rows behind one line of the category mapper, while its popup is open. Null when it is
	 * closed, so nothing on the screen has to hold a page of rows nobody is looking at.
	 */
	categoryRows: null,
	/** A balance upload's own detail: the reconciliation and what backfilling it would write. */
	reconciliation: [],
	backfill: [],
	/** The transaction upload the reconciliation was run against, and the others it could be. */
	comparedAgainst: null,
	comparableBatches: [],
	backfillWritten: null,
	/** What the last commit of this batch wrote, so the screen can say so rather than just refresh. */
	committed: null,
	uploading: false,
	/** A fetch from Monarch is under way. Separate from uploading so each button says what it is doing. */
	fetching: false,
	/**
	 * This budget's Monarch sign-in as Monarch_Connections::describe() gives it -- email, status,
	 * when it last fetched -- or null when it has none. Undefined until the batch list first loads.
	 */
	monarchConnection: undefined,
	/** A sign-in to Monarch is under way. */
	connectingMonarch: false,
	/**
	 * What the last fetch from Monarch did to account balances, as Import_Domain::fetch_monarch
	 * answers it, or null when no fetch has answered since the page last started one.
	 */
	monarchBalances: null,
	/** The Accounts page's "Refresh balances" is under way. */
	refreshingBalances: false,
	/** Why the last balance refresh failed, for the Accounts page; the import page's `error` is its own. */
	refreshError: null,
	/** When, as Date.now(), a refresh refused as too soon may be tried again; null when it may now. */
	refreshBlockedUntil: null,
	/**
	 * Every account Monarch answered the last refresh with, each with its map, as
	 * Import_Domain::refresh_monarch_balances lists them; null until a refresh has answered.
	 */
	monarchAccounts: null,
	loadingAccounts: false,
	loadingPreview: false,
	loadingCategories: false,
	loadingCategoryRows: false,
	loadingBalances: false,
	backfilling: false,
	committing: false,
	error: null,
};

export default (state = initialState, action) => {
	switch(action.type) {
		case IMPORT_UPLOAD_BEGIN:
			return { ...state, uploading: true, error: null };

		case IMPORT_MONARCH_FETCH_BEGIN:
			return { ...state, fetching: true, error: null, monarchBalances: null };

		case IMPORT_MONARCH_BALANCES_RECORDED:
			return { ...state, monarchBalances: action.payload || null };

		case IMPORT_MONARCH_REFRESH_BEGIN:
			return { ...state, refreshingBalances: true, refreshError: null, refreshBlockedUntil: null, monarchBalances: null };

		case IMPORT_MONARCH_REFRESH_SUCCESS:
			return {
				...state,
				refreshingBalances: false,
				monarchAccounts: action.payload.monarch_accounts || state.monarchAccounts,
				availableAccounts: action.payload.available_accounts || state.availableAccounts,
				accountTypes: action.payload.account_types || state.accountTypes,
			};

		/**
		 * A rule saved, cleared or created from the Monarch list: every rule comes back, and each listed
		 * account takes its map from them by name. Rules are Monarch's here, the endpoints' default source.
		 */
		case IMPORT_MONARCH_RULES_LOADED: {
			const rules = {};
			(action.payload.account_rules || []).forEach((rule) => {
				rules[rule.external_key] = rule;
			});
			return {
				...state,
				monarchAccounts: state.monarchAccounts && state.monarchAccounts.map((account) => ({
					...account,
					map: rules[account.external_account] || null,
				})),
				availableAccounts: action.payload.available_accounts || state.availableAccounts,
				accountTypes: action.payload.account_types || state.accountTypes,
			};
		}

		case IMPORT_MONARCH_REFRESH_FAIL:
			return {
				...state,
				refreshingBalances: false,
				refreshError: action.payload,
				refreshBlockedUntil: action.blockedUntil || null,
			};

		/**
		 * Everything Monarch said was about the budget before: its connection, the last fetch's balances,
		 * the accounts a refresh listed, a refresh's refusal or wait. A request still in flight under it
		 * is dropped when it lands (activeBudgetId in the actions), so nothing is left waiting on it.
		 */
		case CHANGE_ACTIVE_BUDGET_SUCCESS:
			return {
				...state,
				monarchConnection: undefined,
				monarchBalances: null,
				monarchAccounts: null,
				refreshingBalances: false,
				refreshError: null,
				refreshBlockedUntil: null,
				connectingMonarch: false,
			};

		case IMPORT_MONARCH_CONNECT_BEGIN:
			return { ...state, connectingMonarch: true, error: null };

		case IMPORT_MONARCH_CONNECTION_CHANGED:
			return { ...state, connectingMonarch: false, monarchConnection: action.payload || null };

		case IMPORT_UPLOAD_SUCCESS:
			return {
				...state,
				uploading: false,
				fetching: false,
				selectedBatch: action.payload.batch,
				accounts: action.payload.accounts || [],
				availableAccounts: action.payload.available_accounts || [],
				accountTypes: action.payload.account_types || state.accountTypes,
			};

		case IMPORT_UPLOAD_FAIL:
			return {
				...state,
				uploading: false,
				fetching: false,
				connectingMonarch: false,
				loadingAccounts: false,
				loadingPreview: false,
				loadingCategories: false,
				loadingCategoryRows: false,
				loadingBalances: false,
				backfilling: false,
				error: action.payload,
			};

		case IMPORT_ERROR_DISMISSED:
			return { ...state, error: null };

		case IMPORT_BATCHES_LOAD_BEGIN:
			return { ...state, error: null };

		case IMPORT_BATCHES_LOAD_SUCCESS:
			return {
				...state,
				batches: action.payload || [],
				monarchConnection: action.monarchConnection === undefined
					? state.monarchConnection
					: action.monarchConnection,
			};

		case IMPORT_BATCH_SELECTED: {
			if(!action.payload) {
				return { ...state, selectedBatch: null, ...clearedDetail() };
			}
			const batch = state.batches.find((candidate) => candidate.id === action.payload);
			return { ...state, selectedBatch: batch || state.selectedBatch };
		}

		case IMPORT_BATCH_DELETED: {
			const survives = state.selectedBatch && state.selectedBatch.id !== action.payload.batchId;
			if(survives) {
				return { ...state, batches: action.payload.batches || [] };
			}
			/** The screen was showing the file that just went away -- clear what described it. */
			return {
				...state,
				batches: action.payload.batches || [],
				selectedBatch: null,
				...clearedDetail(),
			};
		}

		case IMPORT_ACCOUNTS_LOAD_BEGIN:
			return { ...state, loadingAccounts: true };

		case IMPORT_ACCOUNTS_LOAD_SUCCESS:
			return {
				...state,
				loadingAccounts: false,
				selectedBatch: action.payload.batch || state.selectedBatch,
				accounts: action.payload.accounts || [],
				availableAccounts: action.payload.available_accounts || state.availableAccounts,
				accountTypes: action.payload.account_types || state.accountTypes,
			};

		case IMPORT_CATEGORIES_LOAD_BEGIN:
			return { ...state, loadingCategories: true };

		case IMPORT_CATEGORIES_LOAD_SUCCESS:
			return {
				...state,
				loadingCategories: false,
				selectedBatch: action.payload.batch || state.selectedBatch,
				categories: action.payload.categories || [],
				/**
				 * The budget's own categories, unlike the file's, do not depend on which batch this is,
				 * so a response that leaves them out is not a response saying the budget has none.
				 */
				availableCategories: action.payload.available_categories || state.availableCategories,
			};

		case IMPORT_CATEGORY_ROWS_OPENED:
			return {
				...state,
				categoryRows: {
					externalCategory: action.payload.externalCategory,
					/** null asks for the whole category; '' asks for its rows that named no merchant. */
					externalMerchant: action.payload.externalMerchant,
					rows: [],
					total: 0,
					amountTotal: 0,
					offset: 0,
				},
			};

		case IMPORT_CATEGORY_ROWS_CLOSED:
			return { ...state, categoryRows: null, loadingCategoryRows: false };

		case IMPORT_CATEGORY_ROWS_LOAD_BEGIN:
			return { ...state, loadingCategoryRows: true };

		/**
		 * Only if it answers the line still open. Saving a rule re-reads the mapper underneath, and a
		 * popup can be closed or reopened on something else while a page is in flight -- landing those
		 * rows anyway would show one category's transactions under another one's heading.
		 */
		case IMPORT_CATEGORY_ROWS_LOAD_SUCCESS: {
			const open = state.categoryRows;
			const answersTheOpenOne = !!open
				&& open.externalCategory === action.payload.external_category
				&& open.externalMerchant === (action.payload.external_merchant === undefined
					? null
					: action.payload.external_merchant);
			if(!answersTheOpenOne) {
				return { ...state, loadingCategoryRows: false };
			}
			return {
				...state,
				loadingCategoryRows: false,
				categoryRows: {
					...open,
					rows: action.payload.rows || [],
					total: action.payload.total || 0,
					amountTotal: action.payload.amount_total || 0,
					offset: action.payload.offset || 0,
				},
			};
		}

		case IMPORT_BALANCES_LOAD_BEGIN:
			return { ...state, loadingBalances: true };

		case IMPORT_BALANCES_LOAD_SUCCESS:
			return {
				...state,
				loadingBalances: false,
				backfilling: false,
				selectedBatch: action.payload.batch || state.selectedBatch,
				reconciliation: action.payload.reconciliation || [],
				backfill: action.payload.backfill || [],
				comparedAgainst: action.payload.compared_against || null,
				comparableBatches: action.payload.comparable_batches || [],
				/** Only a backfill reports a written count; a plain re-read leaves the last one alone. */
				backfillWritten: action.payload.written === undefined
					? state.backfillWritten
					: action.payload.written,
			};

		case IMPORT_BACKFILL_BEGIN:
			return { ...state, backfilling: true, error: null };

		case IMPORT_PREVIEW_LOAD_BEGIN:
			return { ...state, loadingPreview: true };

		case IMPORT_COMMIT_BEGIN:
			return { ...state, committing: true, error: null };

		/**
		 * The response carries a fresh preview as well as the result, because what was just written
		 * is exactly what the preview no longer has to describe as pending.
		 */
		case IMPORT_COMMIT_SUCCESS:
			return {
				...state,
				committing: false,
				committed: action.payload.committed || null,
				selectedBatch: action.payload.batch || state.selectedBatch,
				previewRows: action.payload.rows || [],
				previewOffset: action.payload.offset || 0,
				previewStatus: action.payload.status || null,
				previewTotal: previewTotal(action.payload),
				summary: action.payload.summary || null,
			};

		case IMPORT_PREVIEW_LOAD_SUCCESS:
			return {
				...state,
				loadingPreview: false,
				selectedBatch: action.payload.batch || state.selectedBatch,
				previewRows: action.payload.rows || [],
				previewOffset: action.payload.offset || 0,
				previewStatus: action.payload.status || null,
				previewTotal: previewTotal(action.payload),
				summary: action.payload.summary || null,
			};

		default:
			return state;
	}
};

/** Everything that described the batch that is no longer on screen. */
function clearedDetail() {
	return {
		accounts: [],
		committed: null,
		previewRows: [],
		previewStatus: null,
		previewTotal: 0,
		summary: null,
		categories: [],
		categoryRows: null,
		reconciliation: [],
		backfill: [],
		comparedAgainst: null,
		comparableBatches: [],
		backfillWritten: null,
	};
}

/** A payload with no total of its own pages the whole file. */
function previewTotal(payload) {
	if(typeof payload.total === 'number') {
		return payload.total;
	}
	return payload.summary ? payload.summary.total : 0;
}
